import React, { useEffect, useState } from 'react';
import { ResetPasswordLoader } from '../Loaders/ResetPasswordLoader';
import useErrorPage from '../../hooks/useErrorPage';
import { useNavigate } from 'react-router-dom';
import { TextInput } from '../Shared/Inputs/TextInput';
import { EmailPasswordModel, EmailPasswordValidation } from '../../models/EmailPasswordModel';
import { Button } from '../Shared/Buttons/Button';
import { getResetPasswordValidity, postResetPassword } from '../../services/RecruiterService';
import { ResponseModel } from '../../models/ResponseModel';
import { PopUpWindow } from "../../components/Shared/PopUpWindow";
import { PasswordValidation } from '../Shared/PasswordValidationPanel';
import { validatePasswordRules } from '../../helpers/Common';

interface Props {
    styles: any;  
    id: string;
    token: string;
    openAlert(status: string, message: string): void;
}

const initialValidation: EmailPasswordValidation = {
    newPassword: {},
    confirmNewPassword: {}
}

export const ResetPassword: React.FC<Props> = (props) => {
    const [loading, setLoading] = useState(false);
    const intialFormInputs: EmailPasswordModel = {
        guid: props.id,
        token: props.token,       
        newPassword: "",  
        confirmNewPassword: ""
    }
    const [validation, setValidation] = useState<EmailPasswordValidation>(initialValidation);
    const navigate = useNavigate();
    const [passwordModel, setPasswordModel] = useState<EmailPasswordModel>(intialFormInputs);
    const [toErrorPage] = useErrorPage();
    const styles = props.styles;
    const [displayInvalid, setDisplayInvalid] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [passwordFocused, setPasswordFocused] = useState<boolean>(false);

    useEffect(() => {         
        validateRequest();
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setPasswordModel((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);
        }
        if (name === "newPassword" && passwordModel.confirmNewPassword !== "") {
            if (value !== passwordModel.confirmNewPassword) {
                setValidation((v: any) => ({
                    ...v,
                    confirmNewPassword: { valid: false, invalid: true, invalidMessage: "Password and confirmation do not match" }
                }));
            } else {
                setValidation((v: any) => ({
                    ...v,
                    confirmNewPassword: { valid: true, invalid: false }
                }));
            }
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);        
    }

    const closeInvalidDialog = () => {
        setDisplayInvalid(false);
        navigate('/recruiter/account/login');
    }

    const validateInput = (name: string, value: string) => {
        if(name === "newPassword") {    
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password is required"}}));
            } 
            else if(!validatePasswordRules(value).isValid) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "confirmNewPassword") {
            const password = passwordModel.newPassword;      
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password confirmation is required"}}));
            } 
            else if (password !== value) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password and confirmation do not match"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }        
    } 

    const validateForm = (): number => {
        let formValidation: EmailPasswordValidation = {
            newPassword: {valid: true},
            confirmNewPassword: {valid: true}
        }
        if(passwordModel.newPassword === "") {
            formValidation.newPassword = {valid: false, invalid: true, invalidMessage: "Password is required"}
        }
        else if(!validatePasswordRules(passwordModel.newPassword ?? "").isValid) {
            formValidation.newPassword = {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"}
        }
        if(passwordModel.confirmNewPassword === "") {
            formValidation.confirmNewPassword = {valid: false, invalid: true, invalidMessage: "Password confirmation is required"}
        }
        else if(passwordModel.newPassword !== passwordModel.confirmNewPassword) {
            formValidation.confirmNewPassword = {valid: false, invalid: true, invalidMessage: "Password and confirmation do not match"};
        }
        setValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key]?.valid).length;
        return validInputCount;
    }

    async function validateRequest() {
        setLoading(true);
        await getResetPasswordValidity(props.id, props.token)
        .then((response: ResponseModel) => {
            setLoading(false);
            if(!response.status) {
                setDisplayInvalid(true);
                setErrorMessage(response.message);
            }
        })      
        .catch((error) => {            
            toErrorPage(error);
        });
    }
    
    async function handleSubmitClick(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault(); 
        let validInputCount = Object.keys(validation).filter(key => validation[key]!.valid).length; 
        if(validInputCount < 2) {
            validInputCount = validateForm();
        }
        if(validInputCount === 2) {
            setLoading(true);
            await postResetPassword(passwordModel)
            .then((response: ResponseModel) => {
                setLoading(false);
                if(response.status) {
                    props.openAlert("success", response.message);
                    navigate('/recruiter/account/login');          
                }
                else {
                    props.openAlert("error", response.message);
                }
            })
            .catch((error) => {            
                toErrorPage(error);
            });
        }  
    }

    return (
        <>
            {loading ? <ResetPasswordLoader isRecruiter={true}/> : 
            <form style={{...styles.passwordForm, ...styles.passwordForm.reset}} onSubmit={handleSubmitClick}>
                <h5 style={styles.title}>Change Password</h5>
                <p style={{...styles.subTitle}}>Please choose a new password.</p>
                <div 
                    onFocus={() => setPasswordFocused(true)} 
                    onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) 
                            setPasswordFocused(false);
                    }}
                >
                    <TextInput
                        label={'New Password *'}
                        inputType={'password'}
                        name={"newPassword"}
                        handleChange={handleInputChange}
                        value={passwordModel.newPassword}
                        handleBlur={handleInputBlur}
                        validation={validation.newPassword}
                    />
                    <PasswordValidation
                        password={passwordModel.newPassword ?? ''}
                        touched={validation.newPassword?.invalid}
                        showStrength
                        focused={passwordFocused}
                    />
                </div>
                <TextInput
                    label={'Confirm New Password *'}
                    inputType={'password'}
                    name={"confirmNewPassword"}
                    handleChange={handleInputChange}
                    value={passwordModel.confirmNewPassword}
                    handleBlur={handleInputBlur}
                    validation={validation.confirmNewPassword}
                />
                <Button type="submit" disabled={loading} style={{...styles.submitButton, ...styles.resetButton}}>{"RESET PASSWORD"}</Button>
            </form>}
            <PopUpWindow styles={styles.invalidDialog} open={displayInvalid} toggle={closeInvalidDialog} closeCircle={true}>
                {errorMessage}
            </PopUpWindow>
        </>
    )
}